(() => {
  'use strict';

  const config = window.HPDBrandModeConfig || {};
  const validModes = new Set(['hikoki', 'metabo', 'both']);
  const html = document.documentElement;
  const vendorFilter = config.vendorFilter || 'filter.p.vendor';
  let mode = validModes.has(html.dataset.hpdBrandMode) ? html.dataset.hpdBrandMode : 'hikoki';
  const originalPageTitle = document.title;
  const originalPageDescription = document.querySelector('meta[name="description"]')?.content || '';

  function normaliseVendor(value) {
    return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  function brandForVendor(vendor) {
    const value = normaliseVendor(vendor);
    if (value.includes('metabo')) return 'metabo';
    if (value.includes('hikoki')) return 'hikoki';
    return '';
  }

  function brandMatchesVendor(vendor, selectedMode = mode) {
    const brand = brandForVendor(vendor);
    if (selectedMode === 'both') return brand === 'hikoki' || brand === 'metabo';
    return brand === selectedMode;
  }

  function vendorsForMode(selectedMode = mode) {
    return Array.isArray(config.vendors?.[selectedMode])
      ? config.vendors[selectedMode]
      : selectedMode === 'metabo' ? ['Metabo'] : ['HiKOKI'];
  }

  function saveMode(selectedMode) {
    try {
      window.localStorage.setItem(config.storageKey || 'hpd_brand_mode_v1', selectedMode);
      document.cookie = `hpd_brand_mode=${encodeURIComponent(selectedMode)}; Max-Age=31536000; Path=/; SameSite=Lax`;
    } catch (error) {
      /* A blocked preference store must not affect shopping. */
    }
  }

  function addBrandFilters(url, selectedMode = mode) {
    const next = new URL(url, window.location.origin);
    next.searchParams.delete(vendorFilter);
    vendorsForMode(selectedMode).forEach((vendor) => next.searchParams.append(vendorFilter, vendor));
    next.searchParams.delete('page');
    return next;
  }

  function routeForBrand(pathname, selectedMode = mode) {
    const path = String(pathname || '').replace(/\/$/, '') || '/';
    const routeGroups = ['service', 'warranty', 'documentation', 'safety'];

    for (const group of routeGroups) {
      const routes = config.routes?.[group];
      if (!routes) continue;
      const candidates = Object.values(routes).map((route) => String(route || '').replace(/\/$/, ''));
      if (candidates.includes(path)) return routes[selectedMode] || routes.hikoki || path;
    }

    return path;
  }

  function setHidden(element, hidden) {
    element.hidden = hidden;
    element.dataset.hpdBrandHidden = hidden ? 'true' : 'false';
    if ('inert' in element) element.inert = hidden;
  }

  function applyBrandOnlyVisibility(root = document) {
    root.querySelectorAll('[data-hpd-brand-only]').forEach((element) => {
      const allowed = String(element.dataset.hpdBrandOnly || '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
      const shouldHide = allowed.length > 0 && !allowed.includes(mode);
      setHidden(element, shouldHide);

      if (element.hasAttribute('data-hpd-collapse-section')) {
        const section = element.closest('.shopify-section');
        if (section) {
          section.dataset.hpdBrandCollapseControlled = 'true';
          setHidden(section, shouldHide);
        }
      }
    });
  }

  function applyRestrictedNavigation(root = document) {
    root.querySelectorAll('a[href]').forEach((anchor) => {
      let path;
      try {
        path = new URL(anchor.href, window.location.origin).pathname.replace(/\/$/, '');
      } catch (error) {
        return;
      }

      const hikokiOnly = path === '/pages/innovation-technology'
        || path === '/pages/hikoki-innovation-technology'
        || path === '/collections/hikoki-deals'
        || path === '/collections/deals-2';
      const container = anchor.hasAttribute('data-hpd-brand-only') ? anchor : (anchor.closest('li') || anchor);
      if (hikokiOnly) {
        const shouldHide = mode === 'metabo';
        container.dataset.hpdBrandNavigationControlled = 'true';
        setHidden(container, shouldHide);
      } else if (container.dataset.hpdBrandNavigationControlled === 'true') {
        setHidden(container, false);
      }
    });
  }

  function applyKitBuilderNavigation(root = document) {
    const label = mode === 'metabo' ? 'Metabo Kit Builder' : mode === 'both' ? 'Kit Builder' : 'HiKOKI Kit Builder';
    root.querySelectorAll('a[href]').forEach((anchor) => {
      let path;
      try {
        path = new URL(anchor.href, window.location.origin).pathname.replace(/\/$/, '');
      } catch (error) {
        return;
      }
      if (path !== '/pages/hikoki-kit-builder') return;
      const container = anchor.hasAttribute('data-hpd-brand-only') ? anchor : (anchor.closest('li') || anchor);
      if (container.dataset.hpdBrandNavigationControlled === 'true') setHidden(container, false);
      const directTextNode = Array.from(anchor.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
      const nestedLabel = Array.from(anchor.children).find((child) => child.tagName === 'SPAN' && !child.classList.contains('visually-hidden'));
      if (directTextNode && directTextNode.textContent.trim() !== label) {
        directTextNode.textContent = ` ${label} `;
      } else if (!directTextNode && nestedLabel && nestedLabel.textContent.trim() !== label) {
        nestedLabel.textContent = label;
      } else if (!directTextNode && !nestedLabel && !anchor.textContent.trim()) {
        anchor.textContent = label;
      }
      anchor.setAttribute('aria-label', label);
    });
  }

  function applySearchInputs(root = document) {
    root.querySelectorAll('form[role="search"], form[action*="/search"]').forEach((form) => {
      const expectedVendors = vendorsForMode();
      const existingInputs = Array.from(form.querySelectorAll('[data-hpd-brand-filter-input]'));
      const existingVendors = existingInputs.map((input) => input.value);
      const isCurrent = existingVendors.length === expectedVendors.length && existingVendors.every((vendor, index) => {
        return vendor === expectedVendors[index];
      });
      if (isCurrent) return;

      existingInputs.forEach((input) => input.remove());
      expectedVendors.forEach((vendor) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = vendorFilter;
        input.value = vendor;
        input.dataset.hpdBrandFilterInput = 'true';
        form.appendChild(input);
      });
    });

    root.querySelectorAll('[data-live-search-input]').forEach((input) => {
      const label = mode === 'metabo' ? 'Metabo' : mode === 'both' ? 'HiKOKI or Metabo' : 'HiKOKI';
      input.placeholder = `Search ${label} tools, model numbers or SKUs`;
    });
  }

  function updateSelectorState(root = document) {
    root.querySelectorAll('[data-hpd-brand-choice]').forEach((button) => {
      const active = button.dataset.hpdBrandChoice === mode;
      button.setAttribute('aria-pressed', String(active));
    });

    root.querySelectorAll('[data-hpd-brand-current]').forEach((element) => {
      const label = mode === 'both' ? 'HiKOKI + Metabo mode' : `${mode === 'metabo' ? 'Metabo' : 'HiKOKI'} mode`;
      if (element.textContent !== label) element.textContent = label;
    });
  }

  function updateHomepageMetadata() {
    if (config.template !== 'index') return;
    const title = document.querySelector('title');
    const description = document.querySelector('meta[name="description"]');
    if (mode === 'metabo') {
      if (title) title.textContent = 'Metabo Power Tools | HPD';
      if (description) description.content = 'Shop Metabo professional power tools with clear ex-VAT pricing, UK product advice and dependable support from HPD.';
    } else if (mode === 'both') {
      if (title) title.textContent = 'HiKOKI & Metabo Power Tools | HPD';
      if (description) description.content = 'Shop HiKOKI and Metabo professional power tools with clear ex-VAT pricing, UK product advice and dependable support from HPD.';
    } else {
      if (title) title.textContent = originalPageTitle;
      if (description && originalPageDescription) description.content = originalPageDescription;
    }
  }

  function updateKitBuilderMetadata() {
    if (config.templateSuffix !== 'kit-builder') return;
    const title = document.querySelector('title');
    const description = document.querySelector('meta[name="description"]');
    const brandLabel = mode === 'metabo' ? 'Metabo' : mode === 'both' ? 'HiKOKI & Metabo Combined' : 'HiKOKI';
    if (title) title.textContent = `${brandLabel} Kit Builder | HPD`;
    if (description) description.content = mode === 'both'
      ? 'Build a combined HiKOKI and Metabo cordless kit with the correct dedicated battery and charger set for each brand.'
      : `Build a ${brandLabel} cordless power-tool kit and unlock an automatic multi-tool saving.`;
  }

  function updateHomepageCategoryImages(root = document) {
    root.querySelectorAll('[data-hpd-category-image]').forEach((container) => {
      const image = container.querySelector('img');
      if (!image || !container.dataset.hpdMetaboSrc) return;

      if (!image.dataset.hpdDefaultSrc) {
        image.dataset.hpdDefaultSrc = image.getAttribute('src') || '';
        image.dataset.hpdDefaultSrcset = image.getAttribute('srcset') || '';
        image.dataset.hpdDefaultSizes = image.getAttribute('sizes') || '';
        image.dataset.hpdDefaultAlt = image.getAttribute('alt') || '';
      }

      if (mode === 'metabo') {
        if (image.getAttribute('src') === container.dataset.hpdMetaboSrc) return;
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = container.dataset.hpdMetaboSrc;
        image.alt = container.dataset.hpdMetaboAlt || image.dataset.hpdDefaultAlt;
        return;
      }

      if (image.getAttribute('src') === image.dataset.hpdDefaultSrc) return;
      image.src = image.dataset.hpdDefaultSrc;
      if (image.dataset.hpdDefaultSrcset) image.srcset = image.dataset.hpdDefaultSrcset;
      else image.removeAttribute('srcset');
      if (image.dataset.hpdDefaultSizes) image.sizes = image.dataset.hpdDefaultSizes;
      else image.removeAttribute('sizes');
      image.alt = image.dataset.hpdDefaultAlt;
    });
  }

  function updateHomepageCategoryCopy(root = document) {
    root.querySelectorAll('[data-hpd-category-copy]').forEach((element) => {
      if (!element.dataset.hpdDefaultText) element.dataset.hpdDefaultText = element.textContent.trim();
      let desiredText = element.dataset.hpdDefaultText;
      if (mode === 'metabo' && element.dataset.hpdMetaboText) {
        desiredText = element.dataset.hpdMetaboText;
      } else if (mode === 'both' && element.dataset.hpdBothText) {
        desiredText = element.dataset.hpdBothText;
      }
      if (element.textContent.trim() !== desiredText) element.textContent = desiredText;
    });
  }

  function applyMode(root = document) {
    html.dataset.hpdBrandMode = mode;
    updateSelectorState(root);
    applyBrandOnlyVisibility(root);
    applyRestrictedNavigation(root);
    applyKitBuilderNavigation(root);
    applySearchInputs(root);
    updateHomepageMetadata();
    updateKitBuilderMetadata();
    updateHomepageCategoryImages(root);
    updateHomepageCategoryCopy(root);
    html.classList.remove('hpd-brand-routing');
  }

  function listingNeedsNavigation(selectedMode) {
    return config.template === 'collection' || config.template === 'search';
  }

  function navigateListing(url, selectedMode) {
    html.classList.add('hpd-brand-routing');
    window.location.assign(addBrandFilters(url, selectedMode).toString());
  }

  function selectMode(nextMode) {
    if (!validModes.has(nextMode) || nextMode === mode) return;
    mode = nextMode;
    saveMode(mode);
    html.dataset.hpdBrandMode = mode;

    const productBrand = config.productBrand || '';
    if (config.template === 'product' && productBrand && mode !== 'both' && mode !== productBrand) {
      const category = config.productCategoryUrl || config.routes?.allProducts || '/collections/all';
      navigateListing(category, mode);
      return;
    }

    if (listingNeedsNavigation(mode)) {
      navigateListing(window.location.href, mode);
      return;
    }

    const solutionTemplates = new Set([
      'innovation-technology',
      'innovation-solutions',
      'metabo-solutions',
      'metabo-battery-solutions',
      'metabo-cas',
      'metabo-dust-extraction',
      'metabo-safety'
    ]);
    if (solutionTemplates.has(config.templateSuffix) && config.routes?.innovation?.[mode]) {
      window.location.assign(config.routes.innovation[mode]);
      return;
    }

    applyMode(document);
    document.dispatchEvent(new CustomEvent('hpd:brand-mode-change', { detail: { mode } }));
  }

  function stripExistingPredictiveBrand(query) {
    return String(query || '')
      .replace(/^\s*\(?\s*vendor:(?:"?HiKOKI"?|"?Metabo"?)(?:\s+OR\s+vendor:(?:"?HiKOKI"?|"?Metabo"?))?\s*\)?\s+AND\s+/i, '')
      .trim();
  }

  function predictiveBrandQuery(query) {
    const clean = stripExistingPredictiveBrand(query);
    if (!clean) return clean;
    if (mode === 'metabo') return `vendor:Metabo AND ${clean}`;
    if (mode === 'both') return `(vendor:HiKOKI OR vendor:Metabo) AND ${clean}`;
    return `vendor:HiKOKI AND ${clean}`;
  }

  function enhancePredictiveSearch() {
    if (!window.fetch || window.fetch.__hpdBrandModeEnhanced) return;
    const nativeFetch = window.fetch.bind(window);
    const enhancedFetch = (input, init) => {
      const rawUrl = typeof input === 'string' ? input : input?.url;
      if (!rawUrl || !rawUrl.includes('section_id=predictive-search')) return nativeFetch(input, init);

      try {
        const url = new URL(rawUrl, window.location.origin);
        const query = url.searchParams.get('q');
        if (query) url.searchParams.set('q', predictiveBrandQuery(query));
        return nativeFetch(url.toString(), init);
      } catch (error) {
        return nativeFetch(input, init);
      }
    };
    enhancedFetch.__hpdBrandModeEnhanced = true;
    window.fetch = enhancedFetch;
  }

  function handleDocumentClick(event) {
    const selector = event.target.closest('[data-hpd-brand-choice]');
    if (selector) {
      event.preventDefault();
      selectMode(selector.dataset.hpdBrandChoice);
      return;
    }

    const anchor = event.target.closest('a[href]');
    if (!anchor || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    let url;
    try {
      url = new URL(anchor.href, window.location.origin);
    } catch (error) {
      return;
    }
    if (url.origin !== window.location.origin) return;

    const routedPath = routeForBrand(url.pathname, mode);
    if (routedPath !== url.pathname.replace(/\/$/, '')) {
      event.preventDefault();
      url.pathname = routedPath;
      window.location.assign(url.toString());
      return;
    }

    if (url.pathname.includes('/collections/') || url.pathname === '/search') {
      event.preventDefault();
      window.location.assign(addBrandFilters(url, mode).toString());
    }
  }

  function observeDynamicContent() {
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(() => {
        scheduled = false;
        applyMode(document);
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function initialise() {
    enhancePredictiveSearch();
    document.addEventListener('click', handleDocumentClick, true);
    applyMode(document);
    observeDynamicContent();

    window.HPDBrandMode = {
      getMode: () => mode,
      setMode: selectMode,
      brandForVendor,
      brandMatchesVendor,
      addBrandFilters
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})();
